import { ChallengeReward, ObjectId, RewardCriteria, SearchIcon, UserTag } from '../types';
import { DEFAULT_CHALLENGE_REWARD_ICON, ICONS8_KEY } from '../helpers/constants';
import { LitElement, TemplateResult, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { debounce, isDefined, undefinedIfEmpty } from '../helpers/utils';
import { dupeChallengeRewardsExist, validateIcon } from '../helpers/tags-categories';
import { externalGet, logWithAmplitude, throwHttpError } from '../helpers/api/api';

import { Dialog } from '@material/mwc-dialog';
import { StateController } from '../helpers/state/state-management/state-controller';
import { TextField } from '@material/mwc-textfield';
import challengeData from '../helpers/state/challenge-data';
import { getMainPage } from '../helpers/page-name';
import topicData from '../helpers/state/topic-data';

@customElement('challenge-reward-form')
export class ChallengeRewardForm extends LitElement {
    amplitude_callsite: string = 'Challenge Reward Form';



    @query('#iconURL')
    private readonly iconUrl?: TextField;

    @query('#iconUrlSelector')
    private readonly iconUrlSelector!: Dialog;

    @query('#search')
    searchInput!: TextField;

    @query('#any-locations')
    anyLocationsCheckbox!: HTMLInputElement;

    @query('#all-locations')
    allLocationsCheckbox!: HTMLInputElement;

    @property()
    allData: ChallengeReward[] = [];

    @property()
    pickedIcon?: SearchIcon;

    @state()
    selectedIconUrl: string | undefined;

    @property({ type: Object })
    data?: ChallengeReward;

    @state()
    challengeReward?: ChallengeReward;

    @property({ type: String })
    icon: string = DEFAULT_CHALLENGE_REWARD_ICON;

    @state()
    reward_criteria?: RewardCriteria;

    @state()
    reward_criteria_threshold: number = 0;

    @state()
    bonus_points: number = 0;

    @state()
    renderableIconString: string = this.icon;

    @state()
    rotatePreviewCSS: string = '';

    @state()
    iconSearchResults: any[] = [];

    @state()
    isAll: boolean = false;

    @state()
    openTab: string = 'selectIconContent';

    @state()
    limitCategories: boolean = false;

    @state()
    selectedCategoryOrTag: ObjectId | undefined;

    @state()
    selectedLocationId: ObjectId | undefined;

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindChallengeState = new StateController(this, challengeData);

    updated(changedProperties: any) {
        changedProperties.forEach((oldValue: any, propName: any) => {
            if(propName === 'data') {
                this.challengeReward = this.data;
                this.reward_criteria = this.challengeReward?.reward_criteria;
                this.reward_criteria_threshold = this.challengeReward?.reward_criteria_threshold ?? 0;
                this.bonus_points = this.challengeReward?.bonus_points ?? 0;
                this.icon = this.challengeReward?.image_url ?? DEFAULT_CHALLENGE_REWARD_ICON;
                this.isAll = this.reward_criteria_threshold === 0;
                this.selectedCategoryOrTag = this.challengeReward?.selected_category_or_tag_id;
                this.selectedLocationId = this.challengeReward?.selected_location_id;
                this.limitCategories = isDefined(this.selectedCategoryOrTag);
                this.requestUpdate();
            }

            if (propName === 'icon') {
                this.renderableIconString = this.icon;
            }
        });
    }


    onChangeChallengeRewardTitle(e: CustomEvent) {
        const name = (e.target as HTMLInputElement).value;

        const dupeChallengeRewardName = dupeChallengeRewardsExist(this.allData, name);

        if (dupeChallengeRewardName) {
            getMainPage().errorToastElement.showToastAndThrow(`A reward named ${dupeChallengeRewardName} already exists. Please choose a different name.`);
        }

        if (isDefined(this.challengeReward)) {
            this.challengeReward.title = name;

            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.challengeReward
                    }
                })
            );
        }
    }

    onChangeChallengeRewardDescription(e: CustomEvent) {
        const description = (e.target as HTMLInputElement).value;

        if (isDefined(this.challengeReward)) {
            this.challengeReward.description = description;

            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.challengeReward
                    }
                })
            );
        }
    }

    onChangeChallengeRewardCompletionMessage(e: CustomEvent) {
        const message = (e.target as HTMLInputElement).value;

        if (isDefined(this.challengeReward)) {
            this.challengeReward.completion_message = message;

            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.challengeReward
                    }
                })
            );
        }
    }

    onChangeChallengeRewardCompletionUrl(e: CustomEvent) {
        let message = (e.target as HTMLInputElement).value;

        // if it does not start with http:// or https://, add https://
        if (isDefined(undefinedIfEmpty(message)) && !message.startsWith('http://') && !message.startsWith('https://')) {
            message = `https://${message}`;
        }

        if (isDefined(this.challengeReward)) {
            this.challengeReward.completion_url = undefinedIfEmpty(message);

            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.challengeReward
                    }
                })
            );
        }
    }

    onChangeChallengeRewardRedeemability(e: CustomEvent) {
        const isRedeemable = (e.target as HTMLInputElement).checked;

        if (isDefined(this.challengeReward)) {
            this.challengeReward.redeemable = isRedeemable;

            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.challengeReward
                    }
                })
            );
        }
    }


    private async searchForIconInternal() {
        const search = undefinedIfEmpty(this.searchInput?.value) ?? 'award';
        this.iconSearchResults = await this.findIcon(this.amplitude_callsite, search);
    }

    private  async findIcon(amplitude_callsite: string, term: string) {
    if (ICONS8_KEY) {
        const response = await fetch(
            `https://search.icons8.com/api/iconsets/v5/search?term=${term}&token=${ICONS8_KEY}`,
            externalGet()
        );
        if (!response.ok) {
            await throwHttpError('An error occurred while listing icons.', response);
        }

        const icons = (await response.json()).icons as SearchIcon[];
        logWithAmplitude(amplitude_callsite, 'Icon Search', { search_text: term });
        return icons;
    } else {
        throw new Error('ICONS8_KEY can not be found');
    }
}

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    private readonly searchForIcon = debounce(this.searchForIconInternal, 200);

    private async customIconSelected(url: string) {
        const validatedIcon = await validateIcon(url);
        if (!validatedIcon) {
            getMainPage().errorToastElement.showToastAndThrow('The icon URL entered is invalid');
        }
        this.icon = validatedIcon ?? DEFAULT_CHALLENGE_REWARD_ICON;

        if (isDefined(this.challengeReward)) {
            this.challengeReward.image_url = url;

            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.challengeReward
                    }
                })
            );
        }

        logWithAmplitude(this.amplitude_callsite, 'Custom Icon Selected', {'icon_url':  url});
    }

    onChangeRewardCriteriaThreshold(e: any) {
        const reward_criteria_threshold = (e.target as HTMLInputElement).value;
        this.reward_criteria_threshold = reward_criteria_threshold ? Number(reward_criteria_threshold) : 0;

        if (isDefined(this.challengeReward)) {
            this.challengeReward.reward_criteria_threshold = reward_criteria_threshold ? Number(reward_criteria_threshold) : 0;

            // check for multi check in. if multi check in is on and not "once", then display warning if threshold is greater than 1
            if (this.challengeReward.reward_criteria_threshold > 1 && challengeData.challenge?.checkin_frequency_recurrance === 'once') {
                getMainPage().errorToastElement.showToastAndThrow(
                    'The threshold for this reward must be 1 or less when recurrant check-in is not enabled. Please enable recurring check-ins above or set the threshold to 1 or less.'
                );
            }
            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.challengeReward
                    }
                })
            );
        }
    }

    onChangeBonusPoints(e: any) {
        const bonus_points = (e.target as HTMLInputElement).value;
        this.bonus_points = bonus_points ? Number(bonus_points) : 0;

        if (isDefined(this.challengeReward)) {
            this.challengeReward.bonus_points = bonus_points ? Number(bonus_points) : 0;
            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.challengeReward
                    }
                })
            );
        }
    }

    onChangeRewardCriteria(e: Event) {
        const reward_criteria = (e.target as HTMLInputElement).value;
        if (reward_criteria === 'Points' && this.isAll) {
            this.reward_criteria = RewardCriteria.AllPoints;
            this.selectedCategoryOrTag = undefined;
        }
        if (reward_criteria === 'Places' && this.isAll) {
            this.reward_criteria = RewardCriteria.AllPlaces;
        }
        if (reward_criteria === 'Points' && !this.isAll) {
            this.reward_criteria = RewardCriteria.NumberOfPoints;
            this.selectedCategoryOrTag = undefined;
        }
        if (reward_criteria === 'Places' && !this.isAll) {
            this.reward_criteria = RewardCriteria.NumberOfPlaces;
        }
        if (reward_criteria === 'Visits') {
            if (this.reward_criteria === RewardCriteria.VisitsToLocation || this.reward_criteria === RewardCriteria.VisitsToAnyLocation || this.reward_criteria === RewardCriteria.VisitsToAllLocations) {
                return;
            }
            this.reward_criteria = RewardCriteria.VisitsToLocation;

            this.selectedCategoryOrTag = undefined;
            this.isAll = false;
        }

        if (isDefined(this.challengeReward) && isDefined(this.reward_criteria)) {
            this.challengeReward.reward_criteria = this.reward_criteria;
            if (reward_criteria === RewardCriteria.AllPlaces || reward_criteria === RewardCriteria.AllPoints) {
                this.challengeReward.reward_criteria_threshold = 0;
            }

            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.challengeReward
                    }
                })
            );
        }
    }

    private async changeOpenTab(tab: string) {
        this.openTab = tab;
        // if icon url tab is opened, focus on the input and search for icons
        if (tab === 'selectIconContent') {
            this.searchInput?.focus();
            await this.searchForIconInternal();
        }
    }

    private async clickIcon(icon: SearchIcon) {
        this.pickedIcon = icon;
        this.selectedIconUrl = `https://img.icons8.com/${icon.platform}/${icon.commonName}.png`;
        await this.customIconSelected(this.selectedIconUrl);
    }

    async onChangeIconUrl(e: CustomEvent) {
        const iconUrl = e.detail?.imageUrl ?? '';
        const validatedIcon = await validateIcon(e.detail?.imageUrl);

        if (!validatedIcon) {
            getMainPage().errorToastElement.showToastAndThrow('The icon URL entered is invalid');
        }

        this.rotatePreviewCSS = '';
        this.dispatchEvent(new CustomEvent('on-change-reward-image', {
            detail: {
                imageToUpload: undefined,
            },
        }));

        this.icon = validatedIcon ?? DEFAULT_CHALLENGE_REWARD_ICON;

        if (isDefined(this.challengeReward)) {
            this.challengeReward.image_url = iconUrl;

            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    detail: {
                        data: this.challengeReward
                    }
                })
            );
        }

        logWithAmplitude(this.amplitude_callsite, 'Challenge Award Icon URL Changed', {'icon_url':  e.detail.imageUrl});
    }

    async onChangeImage(e: CustomEvent) {
        const imageToUpload = e.detail?.imageToUpload;
        this.rotatePreviewCSS =  e.detail?.rotatePreviewCSS;
        this.icon = URL.createObjectURL(imageToUpload?.file);

        this.dispatchEvent(new CustomEvent('on-change-reward-image', {
            detail: {
                imageToUpload: isDefined(imageToUpload)
                    ? imageToUpload
                    : undefined,
            },
        }));
    }

    cancelImageUpload() {
        this.rotatePreviewCSS = '';
        this.dispatchEvent(new CustomEvent('on-change-reward-image', {
            detail: {
                imageToUpload: undefined,
            },
        }));

        this.iconUrlSelector.close();
    }

    toggleAll() {
        // if it was previously allPoints set it to numberOfPoints
        if (this.isAll && this.reward_criteria === RewardCriteria.AllPoints) {
            this.reward_criteria = RewardCriteria.NumberOfPoints;
            this.isAll = false;
        } else if (this.isAll && this.reward_criteria === RewardCriteria.AllPlaces) {
            this.reward_criteria = RewardCriteria.NumberOfPlaces;
            this.isAll = false;
        } else if (!this.isAll && this.reward_criteria === RewardCriteria.NumberOfPoints) {
            this.reward_criteria = RewardCriteria.AllPoints;
            this.isAll = true;
        } else if (!this.isAll && this.reward_criteria === RewardCriteria.NumberOfPlaces) {
            this.reward_criteria = RewardCriteria.AllPlaces;
            this.isAll = true;
        }

        if (!this.isAll && this.challengeReward) {
            this.challengeReward.reward_criteria_threshold = this.reward_criteria_threshold ?? 1;
        }


        if (isDefined(this.challengeReward)) {
            this.challengeReward.reward_criteria = this.reward_criteria ?? RewardCriteria.AllPoints;
            if (this.isAll) {
                this.challengeReward.reward_criteria_threshold = 0;
            }

            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.challengeReward
                    }
                })
            );
        }
    }

    toggleLimitCategoriesAndTags(e: any) {
        this.limitCategories = !this.limitCategories;
        if (!this.limitCategories) {
            this.selectedCategoryOrTag = undefined;
        }
    }

    /** Render tag options for the eligibility picker, indenting sub-tags so a
     *  reward can be scoped to a whole group or an individual sub-tag (e.g. a
     *  single day within a "Days" group). */
    renderTagOptions(tags: UserTag[], depth = 0): TemplateResult[] {
        return tags.flatMap((tag) => {
            // Recurse into children regardless, but don't render a selectable
            // item for a tag with no id — that would store { $oid: '' }.
            const children = tag.tags && tag.tags.length > 0 ? this.renderTagOptions(tag.tags, depth + 1) : [];
            const id = tag._id?.$oid ?? '';
            if (id === '') return children;
            return [
                html`<mwc-list-item value="${id}">
                    <span style="padding-left: ${depth * 14}px;">${tag.name}</span>
                </mwc-list-item>`,
                ...children,
            ];
        });
    }

    updateCategoriesAndTags(e: Event) {
        const id = (e.target as HTMLInputElement).value;
        this.selectedCategoryOrTag = { $oid: id };

        if (isDefined(this.challengeReward)) {
            this.challengeReward.selected_category_or_tag_id = this.selectedCategoryOrTag;
            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.challengeReward
                    }
                })
            );
        }
    }

    toggleAllLocations(e: any) {
        if (this.reward_criteria === RewardCriteria.VisitsToAllLocations) {
            this.reward_criteria = RewardCriteria.VisitsToLocation;
            this.selectedLocationId = challengeData.challengeLocations[0]?.challenge_location_info?._id;
            this.allLocationsCheckbox.checked = false;
        } else {
            this.reward_criteria = RewardCriteria.VisitsToAllLocations;
            this.selectedLocationId = undefined;
        }

        if (isDefined(this.challengeReward)) {
            this.challengeReward.reward_criteria = this.reward_criteria ?? RewardCriteria.VisitsToAllLocations;
            this.challengeReward.selected_location_id = this.selectedLocationId;
            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.challengeReward
                    }
                })
            );
        }
    }

    toggleAnyLocation(e: any) {
        if (this.reward_criteria === RewardCriteria.VisitsToAnyLocation) {
            this.reward_criteria = RewardCriteria.VisitsToLocation;
            this.selectedLocationId = challengeData.challengeLocations[0]?.challenge_location_info?._id;
            this.anyLocationsCheckbox.checked = false;
        } else {
            this.reward_criteria = RewardCriteria.VisitsToAnyLocation;
            this.selectedLocationId = undefined;
        }
        if (isDefined(this.challengeReward)) {
            this.challengeReward.reward_criteria = this.reward_criteria ?? RewardCriteria.VisitsToAnyLocation;
            this.challengeReward.selected_location_id = this.selectedLocationId;
            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.challengeReward
                    }
                })
            );
        }
    }

    updateLocationId(e: Event) {
        const locationId = (e.target as HTMLInputElement).value;
        this.selectedLocationId = { $oid: locationId };
        if (isDefined(this.challengeReward)) {
            this.challengeReward.selected_location_id = this.selectedLocationId;
            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.challengeReward
                    }
                })
            );
        }
    }

    render() {
        // Resolve via the full tag tree — a reward can be scoped to a parent
        // group or a deep sub-tag, which the one-level `allUserTags` omits.
        const displayName = topicData.categoryOrTagNameById(this.selectedCategoryOrTag?.$oid) ?? '';
        const locationName = `${challengeData.challengeLocations.find(location => location.challenge_location_info?._id?.$oid === this.selectedLocationId?.$oid)?.point_info?.name ?? ''} location`;
        const isVisitType = this.reward_criteria === RewardCriteria.VisitsToLocation || this.reward_criteria === RewardCriteria.VisitsToAnyLocation || this.reward_criteria === RewardCriteria.VisitsToAllLocations;
        return html`
            <span class="edit__inputs-container">
                <span class="edit__inputs">
                    <proxi-textfield outlined autofocus id="name" class="edit__textfield" label="Award Name" @change=${this.onChangeChallengeRewardTitle} value="${this.challengeReward?.title ?? ''}" charcounter minlength="1" maxlength="35" required></proxi-textfield>
                    <proxi-textarea outlined id="description" class="edit__textfield" label="Award Info" @change=${this.onChangeChallengeRewardDescription} value="${this.challengeReward?.description ?? ''}" charcounter maxlength="140"></proxi-textarea>
                </span>
                <div class="edit__icons">
                    <span class="edit__icon">
                        <img
                            class="edit__icon-image-main"
                            src="${this.renderableIconString}"
                            style="transform:${this.rotatePreviewCSS}"
                        >
                    </span>
                    <span class="edit__icon"  @click="${() => { this.iconUrlSelector.open = true; this.searchForIcon();} }">
                        Change Award Icon
                    </span>
                </div>
            </span>
            ${this.challengeReward
                        ? html`
                            <p class="slider-header">Completion Options</p>
                            <proxi-textarea outlined id="description" class="edit__textfield" label="Completion Message - Optional (users will see this after they earn it)" @change=${this.onChangeChallengeRewardCompletionMessage} value="${this.challengeReward?.completion_message ?? ''}" charcounter maxlength="400"></proxi-textarea>
                            <proxi-textfield outlined id="description" class="edit__textfield" label="Completion Url - Optional (users will be able to click this after they earn it)" @change=${this.onChangeChallengeRewardCompletionUrl} value="${this.challengeReward?.completion_url ?? ''}" charcounter maxlength="400"></proxi-textfield>
                            <label style="display: flex; align-items: center; gap: 4px;">
                                <input type="checkbox" @change="${this.onChangeChallengeRewardRedeemability}" ?checked="${this.challengeReward?.redeemable ?? false}">
                                Redeemable
                                <form-tooltip
                                    style="display:flex; align-items: center;"
                                    tipwidth="100px"
                                    leftPadding="40%"
                                    message="If enabled, admins will be able to mark this reward as redeemed for users. This is useful for rewards that are not automatically given, such as a physical item, gift card, or raffle ticket."
                                >
                                    <mwc-icon>info_outline</mwc-icon>
                                </form-tooltip>
                            </label>

                            <div style="display: flex; flex-direction: column; gap: 8px; margin-top: 16px;">
                                <label style="font-weight: 500;">Bonus Points</label>
                                <proxi-textfield
                                    outlined
                                    type="number"
                                    min="0"
                                    .height="${'1.5rem'}"
                                    value="${this.bonus_points}"
                                    @change="${this.onChangeBonusPoints}"
                                    placeholder="0"
                                ></proxi-textfield>
                                <p style="font-size: 12px; color: #666; margin: 0;">Extra points added to the participant's score when this award is earned.</p>
                            </div>

                            <p class="slider-header">Success Criteria</p>
                            <p class="criteria"> Users will recieve this award when they have
                                ${this.reward_criteria === RewardCriteria.NumberOfPlaces || this.reward_criteria === RewardCriteria.AllPlaces || isVisitType ? 'visited' : 'earned'}
                                ${this.reward_criteria === RewardCriteria.VisitsToLocation ? `the ${locationName}` : ''}
                                ${this.reward_criteria === RewardCriteria.VisitsToAnyLocation ? 'any location' : ''}
                                ${this.reward_criteria === RewardCriteria.VisitsToAllLocations ? 'all locations' : ''}
                                ${this.isAll ? 'all' : this.reward_criteria_threshold} ${this.reward_criteria === RewardCriteria.NumberOfPlaces || this.reward_criteria === RewardCriteria.AllPlaces ? 'places' : this.reward_criteria === RewardCriteria.NumberOfPoints || this.reward_criteria === RewardCriteria.AllPoints ? 'points' : 'times'}
                                ${isDefined(this.selectedCategoryOrTag) ? `with the following category/tag:
                                    ${displayName}` : ''}
                            </p>

                            <div class="success-criteria">
                                <span class="criteria_section">
                                    <label>
                                    ${!this.isAll ? html`
                                        <proxi-textfield
                                            outlined
                                            class="input reward-criteria-threshold"
                                            id="reward_criteria"
                                            type="number"
                                            label=""
                                            placeholder=""
                                            style="padding:0"
                                            .height="${'1.5rem'}"
                                            value="${this.reward_criteria_threshold}"
                                            @change="${this.onChangeRewardCriteriaThreshold}"
                                        >
                                        </proxi-textfield>
                                    ` : ''}
                                    ${this.reward_criteria === RewardCriteria.VisitsToLocation || this.reward_criteria === RewardCriteria.VisitsToAnyLocation || this.reward_criteria === RewardCriteria.VisitsToAllLocations ? '' : html`
                                        <input type="checkbox" @change="${this.toggleAll}" ?checked="${this.isAll}">
                                            All
                                        </label>
                                    `}
                                </span>

                                <span class="criteria_section">
                                    <proxi-selectfield
                                        id="rewardCriteriaThreshold"
                                        outlined
                                        .height="${'1.5rem'}"
                                        class="input reward-criteria"
                                        @selected="${this.onChangeRewardCriteria}"
                                        value="${this.reward_criteria === RewardCriteria.AllPoints || this.reward_criteria === RewardCriteria.NumberOfPoints ? 'Points' : this.reward_criteria === RewardCriteria.AllPlaces || this.reward_criteria === RewardCriteria.NumberOfPlaces ? 'Places' : this.reward_criteria === RewardCriteria.VisitsToLocation || this.reward_criteria === RewardCriteria.VisitsToAnyLocation || this.reward_criteria === RewardCriteria.VisitsToAllLocations ? 'Visits' : ''}"
                                    >
                                        <mwc-list-item value="Points">Points</mwc-list-item>
                                        <mwc-list-item value="Places">Places</mwc-list-item>
                                        <mwc-list-item value="Visits">Visits To Location</mwc-list-item>
                                    </proxi-selectfield>
                                    ${isVisitType ? html`
                                        ${this.reward_criteria === RewardCriteria.VisitsToLocation ? html`
                                            <span class="criteria"> Location</span>
                                            <proxi-selectfield
                                                outlined
                                                id="manual_location"
                                                value="${this.selectedLocationId?.$oid ?? challengeData.challengeLocations[0]?.challenge_location_info?._id?.$oid ?? ''}"
                                                .height="${'1.5rem'}"
                                                @selected="${this.updateLocationId}"
                                                >
                                                ${challengeData.challengeLocations.map((location) => html`
                                                    <mwc-list-item value="${location.challenge_location_info?._id?.$oid}">
                                                        ${location.point_info?.name ?? location.challenge_location_info?.location?.search ?? ''}
                                                    </mwc-list-item>
                                                `)}
                                            </proxi-selectfield>
                                        ` : ''}
                                        <span style="display: flex; align-items: center; gap: 4px;">
                                            <input type="checkbox" id="all-locations" @change="${this.toggleAllLocations}" ?checked="${this.reward_criteria === RewardCriteria.VisitsToAllLocations}" ?disabled="${this.reward_criteria === RewardCriteria.VisitsToAnyLocation}">
                                                All Locations
                                        </span>
                                        <span style="display: flex; align-items: center; gap: 4px;">
                                        <input type="checkbox" id="any-locations" @change="${this.toggleAnyLocation}" ?checked="${this.reward_criteria === RewardCriteria.VisitsToAnyLocation}" ?disabled="${this.reward_criteria === RewardCriteria.VisitsToAllLocations}">
                                            Any Location
                                        </span>
                                    ` : ''}
                                </span>
                                ${this.reward_criteria === RewardCriteria.NumberOfPlaces || this.reward_criteria === RewardCriteria.AllPlaces ? html`
                                    <span class="criteria_section">
                                    <label>
                                    <input type="checkbox" @change="${this.toggleLimitCategoriesAndTags}" ?checked="${this.limitCategories}">
                                        Limit Eligible Categories/Tags
                                    </label>
                                    ${this.limitCategories ? html`
                                        <label>
                                            <proxi-selectfield outlined @selected="${this.updateCategoriesAndTags}" style="width: 100%;" value="${this.selectedCategoryOrTag?.$oid ?? ''}">
                                                ${topicData.categories.map(category => html`
                                                    <mwc-list-item value="${category._id?.$oid ?? ''}">${category.name}</mwc-list-item>
                                                `)}
                                                ${this.renderTagOptions(topicData.topLevelUserTags)}
                                            </proxi-selectfield>
                                        </label>
                                    ` : ''}
                                    </span>

                                `: ''}
                            </div>
                            `
                        : ''
                    }
            <mwc-dialog
                id="iconUrlSelector"
                class="dialog edit__dialog"
                hideActions
                scrimClickAction=""
                escapeKeyAction=""
            >
                <h3>
                    Change Icon
                </h3>

                <div class="tab">
                    <span class="tablinks" @click="${async () => await this.changeOpenTab('selectIconContent')}">Select Icon</span>
                    <span class="tablinks" @click="${async () => await this.changeOpenTab('uploadContent')}">Upload Icon</span>
                </div>
                ${this.openTab === 'selectIconContent' ? html`
                    <div id="selectIconContent" class="tabcontent">
                        <mwc-textfield
                            outlined
                            id="search"
                            class="input"
                            label="Search"
                            value=${'award'}
                            @input="${this.searchForIcon}"
                        >
                        </mwc-textfield>
                        <div class="icon-options">
                            ${this.iconSearchResults.length === 0 ? html`
                                <span class="no-results">No results found, try a different search</span>
                            `: html`
                                ${this.iconSearchResults.map(
                                    (icon) => html`
                                        <mwc-button @click="${this.clickIcon.bind(this, icon)}">
                                            <img
                                                src="https://img.icons8.com/${icon.platform}/${icon.commonName}.png"
                                                width="24"
                                                height="24"/>
                                        </mwc-button>
                                    `
                                )}
                            `}
                        </div>
                    </div>
                ` : ''}
                ${this.openTab === 'uploadContent' ? html`
                    <div id="uploadContent" class="tabcontent">
                        <image-upload-component
                            .imageUrl="${this.icon}"
                            autoUpload
                            @retreived-autoupload-url="${this.onChangeIconUrl}"
                            @on-change-image="${this.onChangeImage}"
                            @changed-image-url=${this.onChangeIconUrl}
                        ></image-upload-component>
                    </div>
                ` : ''}

                <div class="icon__actions">
                    <form-button variant="social" @click="${this.cancelImageUpload}">
                        Cancel
                    </form-button>
                    <form-button @click="${() => this.iconUrlSelector.close()}">Save</form-button>
                </div>
            </mwc-dialog>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            .edit__inputs-container {
                display: flex;
            }

            .edit__inputs {
                width: 85%;
            }

            .edit__textfield {
                margin-top: 4px;
            }

            .tablinks {
                cursor: pointer;
                border-bottom: 1px solid #ccc;
            }

            .edit__dialog {
                --mdc-dialog-min-width: 360px;
            }

            .edit__icons {
                margin-left: 8px;
            }

            .edit__icon {
                display: flex;
                font-size: 13px;
                text-decoration: underline;
                cursor: pointer;
            }

            .edit__icon:not(.edit__tag) {
                margin-bottom: 12px;
            }

            .edit__icon.edit__tag {
                flex-direction: column;
            }

            .edit__icon-image {
                padding-right: 4px;
                --mdc-icon-size: 18px;
                --mdc-icon-button-size: 18px;
            }

            .edit__icon-image-main {
                padding-right: 4px;
                margin-left: auto;
                margin-right: auto;
                height: 32px;
                width: 32px;
            }

            .icon__actions {
                padding-top: 18px;
                display: flex;
                justify-content: space-evenly;
            }

            .success-criteria {
                width: 100%;
                display: flex;
                box-sizing: border-box;
                align-items: flex-start;
                justify-content: space-between;
                margin: 15px 0;
                gap: 10px;
            }

            .criteria {
                margin: 0;
                font-size: 0.8rem;
                font-weight: 500;
            }

            .criteria_section {
                flex: 1;
                display: flex;
                flex-direction: column;
                align-items: stretch;
            }

            .reward-criteria-threshold__disabled {
                border: 1px solid var(--theme-input-border-color);
                border-radius: 6px;
                padding: 10px;
                background-color: var(--theme-opacity-background-color);
                opacity: 0.7;
                box-sizing: border-box;
            }

            /* Style the tab */
            .tab {
                display: flex;
                justify-content: space-evenly;
                border-bottom: 1px solid #ccc;
                cursor: pointer;
                margin-bottom: 10px;
            }

            /* Style the tab content */
            .tabcontent {
            padding: 6px 12px;
            max-width: 360px;
            max-height: 360px;
            overflow-y: auto;
            }
        `;
    }

}
